import { ReactNode } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { IToastApi } from '@vivli/shared/infrastructure/interface';

// react-toastify silently no-ops a toast whose toastId is already active. These ids used to be
// fixed literals ('error-toast'), which collapsed *different* messages into whichever arrived
// first — a second rejected file never surfaced while the first rejection was still on screen.
// Hashing the message instead keeps repeats of the same text collapsing (the original intent)
// while letting distinct messages stand on their own. (DEV-407 / DEV-406)
const defaultToastId = (kind: string, msg: ReactNode): string | undefined => {
    if (typeof msg !== 'string' && typeof msg !== 'number') {
        // Element content has no stable text to hash; let react-toastify mint a unique id rather
        // than risk collapsing two unrelated elements.
        return undefined;
    }

    const text = String(msg);
    let hash = 5381;
    for (let i = 0; i < text.length; i++) {
        hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    }

    return `${kind}-${(hash >>> 0).toString(36)}`;
};

export const useToastApi = () => {
    const info = (msg: ReactNode, options?: ToastOptions) =>
        toast.info(msg, {
            toastId: defaultToastId('info', msg),
            ...options
        });

    const error = (msg: ReactNode, options?: ToastOptions) =>
        toast.error(msg, {
            toastId: defaultToastId('error', msg),
            ...options
        });

    const success = (msg: ReactNode, options?: ToastOptions) =>
        toast.success(msg, {
            toastId: defaultToastId('success', msg),
            ...options
        });

    const toastProvider: IToastApi = {
        info,
        error,
        success,
        dismiss: toast.dismiss
    };

    return toastProvider;
};
